import React from 'react';
import { formatPrice } from 'utilities/money';
import { CardNumberElement, Elements, useElements, useStripe } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { paths } from 'config/path_helpers';
import useAuthenticityToken from '@magoosh/utils/use_authenticity_token';
import fetch from 'utilities/fetch';
import { bugsnagNotify } from 'utilities/bugsnag';
import { PrimaryButton, SecondaryButton } from '@magoosh/lib/button';
import StripeCardInputs from '@magoosh/pages/payment_flows/subscription_form/stripe_card_inputs';
import { Formik, FormikHelpers } from 'formik';
import style from '@magoosh/pages/payment_flows/subscription_form/style.module.scss';
import cx from 'classnames';

interface PayRemainingBalanceProps {
  price: number;
  subscriptionName: string;
  subscriptionId: number;
  paymentDetails: {
    cardBrand: string;
    cardLast4: string;
    stripePaymentMethodId: string;
    postalCode: string;
    paymentMethod: string;
    email: string;
  };
  stripeToken: string;
}

const PayRemainingBalance: React.FC<PayRemainingBalanceProps> = (props) => {
  const stripe = useStripe();
  const elements = useElements();

  const authToken = useAuthenticityToken();

  const SUCCESS = 'success';
  const ERROR = 'error';

  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [useSavedMethod, setUseSavedMethod] = React.useState(true);
  const [errorMessage, setErrorMessage] = React.useState(null);
  const [paymentAttemptResult, setPaymentAttemptResult] = React.useState(null);

  const handleConfirmation = (formValues: null | CardFormValues) => {
    setIsSubmitting(true);
    let values = {
      authenticityToken: authToken,
      stripePaymentMethodId: props.paymentDetails.stripePaymentMethodId
    };
    if (formValues) {
      values = { ...values, ...formValues };
    }

    fetch(paths.api.subscriptionPayRemainingBalance(props.subscriptionId), {
      method: 'PUT',
      body: JSON.stringify(values)
    })
      .then((response) => {
        if (response._redirect) {
          window.location.assign(response._redirect);
        } else if (response.actionRequired) {
          // Present the 3D Secure dialogue
          stripe
            .confirmCardPayment(response.clientSecret, {
              payment_method: response.paymentMethodId,
              return_url: response.nextStepUrl
            })
            .then((result) => {
              if (result.error) {
                setErrorMessage(result.error.message);
                setPaymentAttemptResult(ERROR);
                setIsSubmitting(false);
              } else {
                setPaymentAttemptResult(SUCCESS);
                // Give Stripe a couple seconds to finish processing
                setTimeout(() => {
                  window.location.assign(response.nextStepUrl);
                }, 2000);
              }
            })
            .catch((error) => {
              setPaymentAttemptResult(ERROR);
              bugsnagNotify(error);
              setIsSubmitting(false);
            });
        }
      })
      .catch((response) => {
        console.log(response);
        setErrorMessage(response.errors._error);
        setIsSubmitting(false);
      });
  };

  // Create the payment method in Stripe, then pass it to handleConfirmation to submit to Magoosh.
  const onSubmitCard = async (
    values: CardFormValues,
    { setSubmitting, setErrors }: FormikHelpers<CardFormValues>
  ): Promise<void> => {
    setSubmitting(true);

    let updatedValues = { ...values };

    const payload = await stripe.createPaymentMethod({
      type: 'card',
      card: elements.getElement(CardNumberElement),
      // eslint-disable-next-line @typescript-eslint/camelcase
      billing_details: {
        name: values.cardName,
        email: props.paymentDetails.email || null,
        address: {
          postal_code: values.cardZipCode
        }
      }
    });
    if (payload.error) {
      if (payload.error.type === 'card_error' || payload.error.type === 'validation_error') {
        setErrors({ stripePaymentMethodId: payload.error.message });
      } else {
        bugsnagNotify(JSON.stringify(payload.error));
      }
      setSubmitting(false);
      return;
    } else {
      updatedValues = { ...values, stripePaymentMethodId: payload.paymentMethod.id };
    }
    handleConfirmation(updatedValues);
  };

  const renderUseSavedMethod = () => {
    return (
      <div>
        <p className="text-center h4">
          Use the {props.paymentDetails.cardBrand.toLocaleUpperCase()} credit card ending in{' '}
          <b>{props.paymentDetails.cardLast4}</b> to pay the remaining balance?
        </p>
        <div className="d-flex u-margin-V-l justify-content-center">
          <PrimaryButton submitting={isSubmitting} onClick={handleConfirmation}>
            Yes, pay {formatPrice(props.price)}
          </PrimaryButton>
          <SecondaryButton
            disabled={isSubmitting}
            className="u-margin-L-s"
            onClick={() => setUseSavedMethod(false)}>
            No, use a different card
          </SecondaryButton>
        </div>
      </div>
    );
  };

  interface CardFormValues {
    cardZipCode: string;
    cardName: string;
    stripePaymentMethodId: any;
  }

  const initialValues = {
    cardZipCode: '',
    cardName: '',
    stripePaymentMethodId: ''
  };

  const renderCreditCardFields = () => {
    return (
      <div>
        <div className="d-flex u-margin-V-l justify-content-center">
          <SecondaryButton disabled={isSubmitting} onClick={() => setUseSavedMethod(true)}>
            Actually, use my saved method
          </SecondaryButton>
        </div>
        <Formik initialValues={initialValues} onSubmit={onSubmitCard}>
          {({ values, errors, handleSubmit }) => {
            return (
              <>
                <StripeCardInputs errors={errors} />
                <div className="d-flex u-margin-V-l justify-content-center">
                  <PrimaryButton onClick={handleSubmit} submitting={isSubmitting}>
                    Submit Payment
                  </PrimaryButton>
                </div>
              </>
            );
          }}
        </Formik>
      </div>
    );
  };

  return (
    <div>
      <p className="text-center h4">
        Your installment plan has a remaining balance of <b>{formatPrice(props.price)}</b>.
      </p>
      {useSavedMethod ? renderUseSavedMethod() : renderCreditCardFields()}
      {errorMessage && (
        <div className={cx('text-center u-margin-V-m', style.errorMessage)}>{errorMessage.toString()}</div>
      )}
    </div>
  );
};

const Wrapper: React.FC<PayRemainingBalanceProps> = (props) => {
  const stripePromise = loadStripe(props.stripeToken);
  return <Elements stripe={stripePromise}>{props.children}</Elements>;
};

const WrappedPayRemainingBalance: React.FC<PayRemainingBalanceProps> = (props) => {
  return (
    <Wrapper {...props}>
      <PayRemainingBalance {...props} />
    </Wrapper>
  );
};

export default WrappedPayRemainingBalance;
